import {makeImageReference} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {sendEvent} from '@github-ui/hydro-analytics'

import type {CopilotAutocompleteManager} from './copilot-autocompletions'
import type {CopilotChatManager} from './copilot-chat-manager'
import type {CopilotChatState} from './copilot-chat-reducer'
import type {CopilotChatModel} from './copilot-chat-types'

// Should be limited to images that we know we can base64 encode and are supported by our models.
// Right now, this is hardcoded but should be model derivative: This set should be supported by
// GPT 4o and Claude Sonnet 3.5, but Gemini Flash 2.0 doesn't support .gif .
const ACCEPTED_IMAGE_FILE_EXTENSIONS: string[] = ['.gif', '.jpg', '.jpeg', '.png', '.webp']
// This is temporary and a little brittle, but it's a staff ship workaround to avoid model failures when using Gemini.
const GEMINI_IMAGE_FILE_EXTENSIONS: string[] = ['.jpg', '.jpeg', '.png', '.webp']

const ACCEPTED_IMAGE_FILE_MIME_TYPES: string[] = ACCEPTED_IMAGE_FILE_EXTENSIONS.map(extension =>
  extension.replace('.', ''),
).map(extension => `image/${extension}`)
const GEMINI_IMAGE_FILE_MIME_TYPES: string[] = GEMINI_IMAGE_FILE_EXTENSIONS.map(extension =>
  extension.replace('.', ''),
).map(extension => `image/${extension}`)

export class CopilotImageAttacher {
  private state: CopilotChatState
  private manager: CopilotChatManager
  private autocomplete: CopilotAutocompleteManager

  public constructor(state: CopilotChatState, manager: CopilotChatManager, autocomplete: CopilotAutocompleteManager) {
    this.state = state
    this.manager = manager
    this.autocomplete = autocomplete
  }

  public static getAllowedImageFileExtensions(model: CopilotChatModel): string {
    return this.getFileExtensions(model).join(',')
  }

  public static isTypeAllowed(itemType: string, model: CopilotChatModel): boolean {
    itemType = itemType.toLowerCase()
    const mimeTypes = this.getMimeTypes(model)
    return mimeTypes.includes(itemType)
  }

  private static getFileExtensions(model: CopilotChatModel): string[] {
    return model.capabilities.family === 'gemini-2.0-flash'
      ? GEMINI_IMAGE_FILE_EXTENSIONS
      : ACCEPTED_IMAGE_FILE_EXTENSIONS
  }

  private static getMimeTypes(model: CopilotChatModel): string[] {
    return model.capabilities.family === 'gemini-2.0-flash'
      ? GEMINI_IMAGE_FILE_MIME_TYPES
      : ACCEPTED_IMAGE_FILE_MIME_TYPES
  }

  public async attachImageToChatInput(file: File): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const reader = new FileReader()

      // error here if there is already one image uploaded
      const hasImageReference = this.state.currentReferences.some(ref => ref.type === 'image')
      if (hasImageReference) {
        const errorMessage = 'Only one image can be uploaded at a time'
        this.manager.addAmbientError(errorMessage)
        sendEvent('dotcom_chat.vision.error', {type: 'file_limit_reached'})
        reject(new Error(errorMessage))
      }

      const addImageReference = async (name: string, image: string) => {
        const imageRef = makeImageReference(name, image)
        await this.autocomplete.addToReferences(imageRef, this.state)
      }

      reader.onload = loadedFileEvent => {
        // This executes asynchronously after the call to readAsDataURL.
        if (!loadedFileEvent.target || !loadedFileEvent.target.result) return
        const result = loadedFileEvent.target.result
        if (typeof result !== 'string') return
        const base64String = result.split(',')[1] // Strip the data URL prefix
        if (base64String) {
          if (base64String.length > 500_000) {
            const errorMessage = 'Only images below 500KB are supported during the staff ship period'
            this.manager.addAmbientError(errorMessage)
            sendEvent('dotcom_chat.vision.error', {type: 'size_limit_exceeded', size: base64String.length})
            reject(new Error(errorMessage))
            return
          }

          // eslint-disable-next-line github/no-then
          addImageReference(file.name, result).then(
            () => {
              resolve()
            },
            error => {
              if (typeof error === 'string') {
                reject(new Error(error))
              } else if (error instanceof Error) {
                reject(error)
              }
              reject(new Error('Unknown error occurred while trying to add image reference.'))
            },
          )
        } else {
          reject(new Error("Couldn't read the file as a Base64 string"))
        }
      }

      reader.onerror = fileErrorEvent => {
        // This executes asynchronously after the call to readAsDataURL.
        if (fileErrorEvent.target) {
          const error = fileErrorEvent.target.error
          if (error) {
            // since this is kicking off as a staff ship, it should be safe to expose the error name and message
            this.manager.addAmbientError(`An error occurred while reading the file: ${error.name} - ${error.message}`)
            sendEvent('dotcom_chat.vision.error', {type: error.name, message: error.message})
            reject(error)
            return
          }
        }
        reject(new Error("Couldn't read the file due to an unknown error"))
      }

      reader.readAsDataURL(file) // Reads the file as a data URL, which includes the Base64-encoded string
    })
  }
}
